/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import PAMediaCleaningHeader from "./partials/PAMediaCleaningHeader";
import { useEffect, useMemo, useState } from "react";
import PABatchSelection from "./partials/PABatchSelection";
import PAExecutionDetails from "./partials/PAExecutionDetails";
import MediaPrepTable from "../../../common/commonComponents/lotDetailsTable";
import { getPAMediaCleaningColumns } from "./partials/columns";
import {
	CleaningAgent,
	CleaningAgentResponse,
	PAContainerCleaning,
	PAContainerCleaningListResponse,
} from "./mediaCleaning.types";
import { postAutoclaveServices } from "../../../../service/postAutoclaveServices";
import {
	PAMediaBatch,
	PAMediaRemovalBatchListResponse,
} from "../mediaRemoval/mediaRemoval.types";
import PAMediaCleaningTableHeader from "./partials/PAMediaCleaningTableHeader";
import PACleaningStatCard from "./partials/PACleaningStatCard";
import PACleaningFooter from "./partials/PACleaningFooter";
import PACleaningConfirmationModal from "./partials/PACleaningConfirmationModal";
import CustomToast from "../../../../widgets/toaster";
import PAReCleaningConfirmationModal from "./partials/PAReCleaningConfimationModal";

const MediaCleaning = () => {
	const { t } = useTranslation();

	const [mediaBatches, setMediaBatches] = useState<PAMediaBatch[]>([]);
	const [selectedBatch, setSelectedBatch] = useState<PAMediaBatch>();
	const [containers, setContainers] = useState<PAContainerCleaning[]>([]);
	const [cleaningMethod, setCleaningMethod] = useState<string>("Manual");
	const [cleaningAgents, setCleaningAgents] = useState<CleaningAgent[]>([]);
	const [selectedCleaningAgent, setSelectedCleaningAgent] =
		useState<CleaningAgent>();
	const [sopReference, setSopReference] = useState("SOP-LAB-CLN-042");
	const [filter, setFilter] = useState<string>("all");
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showValidation, setShowValidation] = useState(false);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});
	const [errors, setErrors] = useState({
		batch: "",
		cleaningAgent: "",
		table: "",
		containers: {} as Record<string, string>,
	});


	const validate = () => {
	const newErrors = {
		batch: "",
		cleaningAgent: "",
		table: "",
		containers: {} as Record<string, string>,
	};
	let isValid = true;

	if (!selectedBatch) {
		newErrors.batch = t("postAutoclaveProcess.errors.batch");
		isValid = false;
	}
	// Cleaning agent validation
	if (!selectedCleaningAgent) {
		newErrors.cleaningAgent = t("postAutoclaveProcess.errors.cleaningAgent");
		isValid = false;
	}
	if (containers.length === 0) {
		newErrors.table = t("postAutoclaveProcess.errors.plates");
		isValid = false;
	}
	// Container validation
	containers.forEach((container) => {
		// Status mandatory
		if (!container.visual_cleanliness) {
		newErrors.containers[container.container_id] =t("postAutoclaveProcess.errors.passOrFail");
		isValid = false;
		}

		// Remarks mandatory only for Fail
		if (
		container.visual_cleanliness === "Fail" &&
		!container.remarks?.trim()
		) {
		newErrors.containers[container.container_id] =
			"Remarks are required";
		isValid = false;
		}
	});

	setErrors(newErrors);
	return isValid;
	};

	const [showRecleaningConfirmationModal, setShowRecleaningConfirmationModal] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(false);

	const getMediaBatchList = async () => {
		try {
			const urlParams = {};
			const response: PAMediaRemovalBatchListResponse =
				await postAutoclaveServices.getMediaBatches(urlParams);
			setMediaBatches(response.data);
			setSelectedBatch(undefined);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainers = async () => {
		if (!selectedBatch) return;
		try {
			const response: PAContainerCleaningListResponse =
				await postAutoclaveServices.getMediaCleaningContainerList({
					sort: "id",
					order: "desc",
					media_batch_id: selectedBatch?.id,
				});
			setContainers(response.data);
			setStats({
				total: response.statistics.total_plates,
				passed: response.statistics.passed,
				failed: response.statistics.failed,
				eligible: response.statistics.eligible_for_next_step,
				requireReClean: response.statistics.require_recleaning,
				maxAttemptReached: response.statistics.max_attempt_reached,
				pending:
					response.statistics.total_plates -
					response.statistics.passed -
					response.statistics.failed,
				allAssessed:
					response.statistics.total_plates -
						response.statistics.passed -
						response.statistics.failed ===
					0,
				});
		} catch (error) {
			console.log(error);
		}
	};

	const getCleaningAgents = async () => {
		try {
			const response: CleaningAgentResponse =
				await postAutoclaveServices.getContainerCleaningAgents({
					page: "1",
					size: "50",
					sort: "id",
					order: "desc",
					status: "active",
				});
			setCleaningAgents(response.data);
		} catch (error) {
			console.log(error);
		}
	};

	const handleConfirm = () => {
	if (!validate()) {
		setShowValidation(true);
		return;
	}

	setShowValidation(false);
	setShowConfirmation(true);
	};

	const handleResetAll = () => {
		setContainers((prevContainers) =>
			prevContainers.map((cont) => ({
				...cont,
				visual_cleanliness: "",
				remarks: "",
			})),
		);
	};

	const handlePassAll = () => {
		setContainers((prevContainers) =>
			prevContainers.map((cont) => ({
				...cont,
				visual_cleanliness: "Pass",
			})),
		);
	};

	const handleReclean = () => {
	if (!validate()) {
		setShowValidation(true);
		return;
	}
	setShowValidation(false);
	setShowRecleaningConfirmationModal(true);
	};

	const handleRemarksChange = (row: PAContainerCleaning, remarks: string) => {
		setContainers((prevContainers) =>
			prevContainers.map((cont) =>
				cont.container_id === row.container_id
					? { ...cont, remarks }
					: cont,
			),
		);
	};

	const handleStatusChange = (row: PAContainerCleaning, status: string) => {
		setContainers((prevContainers) =>
			prevContainers.map((cont) =>
				cont.container_id === row.container_id
					? { ...cont, visual_cleanliness: status }
					: cont,
			),
		);
	};

	const handleSuccess = () => {
		setRefreshTrigger((prev) => !prev);
		setSelectedCleaningAgent(undefined);
	};

	const filteredContainers = useMemo(() => {
		switch (filter) {
			case "pass":
				return containers.filter((c) => c.visual_cleanliness === "Pass");
			case "fail":
				return containers.filter((c) => c.visual_cleanliness === "Fail");
			default:
				return containers;
		}
	}, [containers, filter]);

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const [stats, setStats] = useState({
		total: 0,
		passed: 0,
		failed: 0,
		pending: 0,
		eligible: 0,
		requireReClean: 0,
		allAssessed: false,
		maxAttemptReached: 0,
	});

	const currentStats = useMemo(() => {
		const newlyPassed = containers.filter(
			(c) => c.visual_cleanliness === "Pass"
		).length;

		const newlyFailed = containers.filter(
			(c) => c.visual_cleanliness === "Fail"
		).length;

		const passed = stats.passed + newlyPassed;
		const failed = stats.failed + newlyFailed;
		const pending = stats.total - passed - failed;

		return {
			...stats,
			passed,
			failed,
			pending,
			allAssessed: pending === 0,
		};
	}, [containers, stats]);


	const STAT_CARDS = [
		{
			id: "all",
			label: t("postAutoclaveProcess.mediaCleaning.total"),
			sublabel: t("postAutoclaveProcess.mediaCleaning.totalPlates"),
			icon: "inventory_2",
			variant: "default",
			value: currentStats.total,
		},
		{
			id: "pass",
			label: t("postAutoclaveProcess.mediaCleaning.passed"),
			sublabel: t("postAutoclaveProcess.mediaCleaning.passed"),
			icon: "check_circle",
			variant: "success",
			value: currentStats.passed,
		},
		{
			id: "fail",
			label: t("postAutoclaveProcess.mediaCleaning.failed"),
			sublabel: t("postAutoclaveProcess.mediaCleaning.failed"),
			icon: "cancel",
			variant: "danger",
			value: currentStats.failed,
		},
		{
			id: null,
			label: t("postAutoclaveProcess.mediaCleaning.eligible"),
			sublabel: t("postAutoclaveProcess.mediaCleaning.eligibileForNextStep"),
			icon: "forward",
			variant: "info",
			value: currentStats.eligible,
		},
		{
			id: null,
			label: t("postAutoclaveProcess.mediaCleaning.reclean"),
			sublabel: t("postAutoclaveProcess.mediaCleaning.requireReclean"),
			icon: "refresh",
			variant: "warning",
			value: currentStats.requireReClean,
		},
	] as const;

	useEffect(() => {
		getMediaBatchList();
		getCleaningAgents();
	}, []);

	useEffect(() => {
		getContainers();
	}, [selectedBatch, refreshTrigger]);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("postAutoclaveProcess.title")} pageDescription={t('pageDescription.postAutoclave.description')}/>
			<div className="flex flex-col h-full bg-[#F8FAFC] font-['Inter'] relative overflow-hidden">
				<PAMediaCleaningHeader />

				<div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">
					<PABatchSelection
						availableBatches={mediaBatches}
						selectedBatch={selectedBatch}
						setSelectedBatch={(batch) => {
							setSelectedBatch(batch);

							setErrors((prev) => ({
								...prev,
								batch: "",
							}));
						}}
						platesCount={stats.total}
						errors={errors}
					/>
					<PAExecutionDetails
						setSelectedCleaningAgent={(cleaningAgent: CleaningAgent) =>
							setSelectedCleaningAgent(cleaningAgent)
						}
						setCleaningMethod={setCleaningMethod}
						setSopReference={setSopReference}
						cleaningMethod={cleaningMethod}
						cleaningAgents={cleaningAgents}
						sopReference={sopReference}
						selectedCleaningAgent={selectedCleaningAgent}
						errors={errors}
    					setErrors={setErrors}
					/>
					<div className="grid grid-cols-5 gap-6">
						{STAT_CARDS.map(
							({ id, label, sublabel, icon, variant, value }) => (
								<PACleaningStatCard
									key={label}
									label={label}
									sublabel={sublabel}
									value={value}
									icon={icon}
									variant={variant}
									active={id ? filter === id : false}
									clickable={!!id}
									onClick={id ? () => setFilter(id) : undefined}
								/>
							),
						)}
					</div>
					<div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[250px]">
						<PAMediaCleaningTableHeader
							handlePassAll={handlePassAll}
							handleResetAll={handleResetAll}
							filter={filter}
							setFilter={setFilter}
						/>
						<MediaPrepTable
							columns={getPAMediaCleaningColumns(
								t,
								handleStatusChange,
								handleRemarksChange,
								errors
							)}
							data={filteredContainers}
							emptyMessage={"No plates to display"}
						/>
						{errors.table && (
							<div className="px-8 py-2">
								<p className="text-[11px] text-red-500">
									{errors.table}
								</p>
							</div>
						)}
					</div>
					{/* <div className="grid grid-cols-5 gap-6">
						{STAT_CARDS.map(
							({ id, label, sublabel, icon, variant, value }) => (
								<PACleaningStatCard
									key={label}
									label={label}
									sublabel={sublabel}
									value={value}
									icon={icon}
									variant={variant}
									active={id ? filter === id : false}
									clickable={!!id}
									onClick={id ? () => setFilter(id) : undefined}
								/>
							),
						)}
					</div> */}
				</div>
				<PACleaningFooter
					total={currentStats.total}
					passed={currentStats.passed}
					failed={currentStats.failed}
					allAssessed={currentStats.allAssessed}
					toggleConfirmationModal={handleConfirm}
					toggleRecleanModal={handleReclean}
				/>

				{showConfirmation && selectedCleaningAgent && (
					<PACleaningConfirmationModal
						passed={currentStats.passed}
						failed={currentStats.failed}
						showToast={showToast}
						toggleConfirmationModal={() => setShowConfirmation(false)}
						cleaningMethod={cleaningMethod}
						cleaningAgentId={selectedCleaningAgent.id}
						containers={containers}
						mediaBatchId={selectedBatch?.id ?? ""}
						handleSuccess={handleSuccess}
					/>
				)}
				{showRecleaningConfirmationModal && selectedCleaningAgent && (
					<PAReCleaningConfirmationModal
					   passed={currentStats.passed}
						showToast={showToast}
						toggleRecleanModel={() => setShowRecleaningConfirmationModal(false)}
						handleSuccess={handleSuccess}
						failed={currentStats.failed}
						cleaningMethod={cleaningMethod}
						mediaBatchId={selectedBatch?.id ?? ""}
						cleaningAgentId={selectedCleaningAgent.id}
						containers={containers}
					/>
				)}
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={200}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default MediaCleaning;
